import { useEffect, useState, type ReactNode } from 'react';

/**
 * Lays the kiosk out at the panel's real size and scales it to fit the window.
 *
 * The kiosk is a 49" portrait signage board. Previewing it in a desktop browser
 * previously meant the layout reflowed to whatever shape the window happened to
 * be, so what you reviewed was never what the unit would show — type sized for
 * a guest standing a metre back looked absurd in a laptop window, and a landscape
 * window silently produced a layout that cannot exist on the hardware.
 *
 * So the stage is a fixed KIOSK_W x KIOSK_H box and everything inside is laid
 * out against that, then the whole thing is transform-scaled down to fit. On the
 * unit itself the scale comes out at 1 and the stage fills the panel exactly.
 *
 * `--app-h` is what makes this work: screens read their full height from that
 * variable rather than from `dvh`. Inside a scaled stage `dvh` is still the
 * browser window, so every full-height screen would be the wrong size.
 *
 * Scaling does not touch the recording. MediaRecorder captures the compositor
 * canvas directly, not the DOM, so the clip is unaffected by a CSS transform —
 * and pointer coordinates are mapped through the transform by the browser, so
 * touch targets still land where they look.
 */

/**
 * The panel's CSS viewport, per docs/KIOSK_HARDWARE.md.
 *
 * Marked UNVERIFIED there: 1080x1920 is derived from an assumed 4K panel at
 * DPR 2, and nothing has measured it on the unit. `/ar-conformance.html` reports
 * the real numbers. If they differ, change these two constants — the stage will
 * then letterbox on the hardware, which is a visible signal rather than a silent
 * reflow.
 */
export const KIOSK_W = 1080;
export const KIOSK_H = 1920;

export function KioskFrame({ children }: { children: ReactNode }) {
  const [scale, setScale] = useState(() => fitScale());

  useEffect(() => {
    const onResize = () => setScale(fitScale());
    window.addEventListener('resize', onResize);
    // Orientation changes on a tablet report the old size on the resize event
    // in some browsers, so listen for both.
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  return (
    <div className="fixed inset-0 overflow-hidden bg-black">
      {/*
        Centred by absolute position and a translate, not by flex or grid
        centring. The stage is 1920px tall and the window usually is not, and an
        auto grid row grows to fit its item — so `place-items-center` centred the
        stage inside a 1920px row rather than inside the window, and it hung off
        the bottom. Percentages in translate resolve against the element's own
        unscaled box, which is what makes this land dead centre at any scale.
      */}
      <div
        className="absolute left-1/2 top-1/2"
        style={{
          width: KIOSK_W,
          height: KIOSK_H,
          // The stage is the height every screen inside measures itself against.
          ['--app-h' as string]: `${KIOSK_H}px`,
          transform: `translate(-50%, -50%) scale(${scale})`,
          transformOrigin: 'center',
        }}
      >
        {children}
      </div>
    </div>
  );
}

/** Contain, not cover — the whole panel has to be visible to review it. */
function fitScale(): number {
  if (typeof window === 'undefined') return 1;
  return Math.min(window.innerWidth / KIOSK_W, window.innerHeight / KIOSK_H);
}
